import React, { useState, useEffect } from 'react';
import { X, FileText, Calendar, CheckCircle, ChevronRight, Download, Clock, AlertTriangle } from 'lucide-react';
import { Client, WorkOrder, WorkOrderStatus } from '../types';
import { isOrderInDateRange, getDatePresets } from '@/utils/dateUtils';
import { useSubmitLock } from '@/hooks/useSubmitLock';
import { getOrderDisplayTitle } from '@/utils/workSiteUtils';

interface InvoiceModalProps {
    isOpen: boolean;
    client: Client | null;
    orders: WorkOrder[];
    onClose: () => void;
    onGeneratePDF: (client: Client, selectedOrders: string[], dateStart?: string, dateEnd?: string) => Promise<void> | void;
}

export const InvoiceModal: React.FC<InvoiceModalProps> = ({ isOpen, client, orders, onClose, onGeneratePDF }) => {
    const [dateStart, setDateStart] = useState('');
    const [dateEnd, setDateEnd] = useState('');
    const [statusFilter, setStatusFilter] = useState<'all' | 'completed' | 'in_progress'>('all');
    const [selectedOrders, setSelectedOrders] = useState<string[]>([]);
    const { isLocked: isGenerating, runWithLock: runWithGenerateLock } = useSubmitLock();

    useEffect(() => {
        if (isOpen && client) {
            // Default: seleccionar todos los partes finalizados del cliente
            const completedIds = orders
                .filter(o => o.clientId === client.id && o.status === WorkOrderStatus.COMPLETED && !o.isArchived)
                .map(o => o.id);
            setSelectedOrders(completedIds);
            setDateStart('');
            setDateEnd('');
            setStatusFilter('all');
        }
    }, [isOpen, client, orders]);

    if (!isOpen || !client) return null;

    // Incluir COMPLETED e IN_PROGRESS con al menos una jornada registrada
    const clientOrders = orders.filter(o => {
        if (o.clientId !== client.id || o.isArchived) return false;
        const isCompleted = o.status === WorkOrderStatus.COMPLETED;
        const isInProgress = o.status === WorkOrderStatus.IN_PROGRESS && (o.laborJournal?.length ?? 0) > 0;
        if (!isCompleted && !isInProgress) return false;

        // Filtro de estado
        if (statusFilter === 'completed' && !isCompleted) return false;
        if (statusFilter === 'in_progress' && !isInProgress) return false;

        // Filtro de fechas robusto y seguro
        if (dateStart || dateEnd) {
            if (!isOrderInDateRange(o, { start: dateStart || undefined, end: dateEnd || undefined })) {
                return false;
            }
        }

        return true;
    });

    const toggleOrder = (id: string) => {
        setSelectedOrders(prev =>
            prev.includes(id) ? prev.filter(oid => oid !== id) : [...prev, id]
        );
    };

    const hasInProgressSelected = clientOrders
        .filter(o => selectedOrders.includes(o.id))
        .some(o => o.status === WorkOrderStatus.IN_PROGRESS);

    const formatDate = (iso?: string) => {
        if (!iso) return '';
        return new Date(iso).toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' });
    };

    return (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <div className="bg-white dark:bg-dark-card rounded-2xl w-full max-w-lg p-6 shadow-2xl overflow-y-auto max-h-[90vh] animate-in zoom-in-95 duration-200 border border-slate-200 dark:border-dark-border">
                <div className="flex justify-between items-center mb-6">
                    <div>
                        <h3 className="text-xl font-bold text-slate-900 dark:text-white">Generar Informe</h3>
                        <p className="text-slate-500 text-xs">Selecciona las órdenes de trabajo para el PDF</p>
                    </div>
                    <button onClick={onClose} className="p-1 bg-slate-100 dark:bg-dark-border rounded-full hover:bg-slate-200 dark:hover:bg-slate-600"><X className="w-5 h-5 text-slate-400" /></button>
                </div>

                <div className="space-y-4">
                    {/* Filtro de Estado */}
                    <div className="p-3 bg-white dark:bg-dark-input rounded-xl border border-slate-200 dark:border-dark-border">
                        <p className="text-xs font-bold text-slate-500 uppercase mb-2 px-1">Filtro de Estado</p>
                        <div className="flex gap-2">
                            {([
                                { key: 'all', label: 'Todos' },
                                { key: 'completed', label: 'Finalizados' },
                                { key: 'in_progress', label: 'En Curso' },
                            ] as const).map(({ key, label }) => (
                                <button
                                    key={key}
                                    onClick={() => setStatusFilter(key)}
                                    className={`flex-1 py-1.5 text-xs font-bold rounded-lg border transition-all ${
                                        statusFilter === key
                                            ? key === 'in_progress'
                                                ? 'bg-orange-500 border-orange-500 text-white'
                                                : key === 'completed'
                                                ? 'bg-emerald-600 border-emerald-600 text-white'
                                                : 'bg-primary border-primary text-white'
                                            : 'bg-white dark:bg-dark-card border-slate-200 dark:border-dark-border text-slate-600 dark:text-slate-400 hover:border-slate-400'
                                    }`}
                                >
                                    {label}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Filtro de fechas */}
                    <div className="p-4 bg-white dark:bg-dark-input rounded-xl border border-slate-200 dark:border-dark-border">
                        <div className="flex justify-between items-center mb-2 px-1">
                            <p className="text-xs font-bold text-slate-500 uppercase">Filtro de Fechas (Período a Facturar)</p>
                            {(dateStart || dateEnd) && (
                                <button
                                    type="button"
                                    onClick={() => { setDateStart(''); setDateEnd(''); }}
                                    className="text-[10px] text-primary hover:underline font-semibold"
                                >
                                    Limpiar
                                </button>
                            )}
                        </div>

                        {/* Quick Presets */}
                        <div className="grid grid-cols-4 gap-1.5 mb-3">
                            <button
                                type="button"
                                onClick={() => {
                                    const p = getDatePresets().today;
                                    setDateStart(p.start);
                                    setDateEnd(p.end);
                                }}
                                className="py-1 px-1 text-[10px] font-bold rounded-lg bg-slate-100 dark:bg-dark-card hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300 border border-slate-200 dark:border-dark-border transition-colors text-center"
                            >
                                Hoy
                            </button>
                            <button
                                type="button"
                                onClick={() => {
                                    const p = getDatePresets().thisWeek;
                                    setDateStart(p.start);
                                    setDateEnd(p.end);
                                }}
                                className="py-1 px-1 text-[10px] font-bold rounded-lg bg-slate-100 dark:bg-dark-card hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300 border border-slate-200 dark:border-dark-border transition-colors text-center"
                            >
                                Semana
                            </button>
                            <button
                                type="button"
                                onClick={() => {
                                    const p = getDatePresets().thisMonth;
                                    setDateStart(p.start);
                                    setDateEnd(p.end);
                                }}
                                className="py-1 px-1 text-[10px] font-bold rounded-lg bg-slate-100 dark:bg-dark-card hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300 border border-slate-200 dark:border-dark-border transition-colors text-center"
                            >
                                Mes
                            </button>
                            <button
                                type="button"
                                onClick={() => {
                                    const p = getDatePresets().last30Days;
                                    setDateStart(p.start);
                                    setDateEnd(p.end);
                                }}
                                className="py-1 px-1 text-[10px] font-bold rounded-lg bg-slate-100 dark:bg-dark-card hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300 border border-slate-200 dark:border-dark-border transition-colors text-center"
                            >
                                30 Días
                            </button>
                        </div>

                        <div className="grid grid-cols-2 gap-3">
                            <div className="relative">
                                <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500 pointer-events-none" />
                                <input
                                    type="date"
                                    className="w-full pl-9 pr-2 py-2 bg-white dark:bg-dark-card border border-slate-200 dark:border-dark-border rounded-lg text-slate-900 dark:text-white text-xs outline-none"
                                    value={dateStart}
                                    max={dateEnd || undefined}
                                    onChange={e => {
                                        const val = e.target.value;
                                        if (dateEnd && val > dateEnd) {
                                            setDateStart(val);
                                            setDateEnd('');
                                        } else {
                                            setDateStart(val);
                                        }
                                    }}
                                />
                            </div>
                            <div className="relative">
                                <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500 pointer-events-none" />
                                <input
                                    type="date"
                                    className="w-full pl-9 pr-2 py-2 bg-white dark:bg-dark-card border border-slate-200 dark:border-dark-border rounded-lg text-slate-900 dark:text-white text-xs outline-none"
                                    value={dateEnd}
                                    min={dateStart || undefined}
                                    onChange={e => setDateEnd(e.target.value)}
                                />
                            </div>
                        </div>
                    </div>

                    {/* Lista de órdenes */}
                    <div className="bg-white dark:bg-dark-input rounded-xl border border-slate-200 dark:border-dark-border overflow-hidden">
                        <div className="p-3 bg-white dark:bg-dark-card border-b border-slate-200 dark:border-dark-border flex justify-between items-center">
                            <span className="text-xs font-bold text-slate-400 uppercase">Partes del Cliente</span>
                            <span className="text-[10px] text-slate-500">{selectedOrders.length} de {clientOrders.length} seleccionados</span>
                        </div>
                        <div className="max-h-60 overflow-y-auto p-2 space-y-2">
                            {clientOrders.map(order => {
                                const isInProgress = order.status === WorkOrderStatus.IN_PROGRESS;
                                const isSelected = selectedOrders.includes(order.id);
                                return (
                                    <div
                                        key={order.id}
                                        onClick={() => toggleOrder(order.id)}
                                        className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-all ${isSelected ? 'bg-primary/10 border-primary/50 shadow-sm' : 'bg-white dark:bg-dark-card border-slate-200 dark:border-dark-border hover:border-slate-400 dark:hover:border-slate-600'}`}
                                    >
                                        <div className={`shrink-0 w-5 h-5 mt-0.5 rounded flex items-center justify-center border transition-colors ${isSelected ? 'bg-primary border-primary' : 'border-slate-300 dark:border-slate-600'}`}>
                                            {isSelected && <CheckCircle className="w-4 h-4 text-white" />}
                                        </div>
                                        <div className="min-w-0 flex-1">
                                            <div className="flex justify-between items-start gap-2">
                                                <p className="text-slate-900 dark:text-white text-xs font-bold truncate">{getOrderDisplayTitle(order, { prefer: 'description' })}</p>
                                                {isInProgress ? (
                                                    <span className="shrink-0 flex items-center gap-1 px-1.5 py-0.5 bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400 rounded-full text-[9px] font-bold uppercase">
                                                        <Clock className="w-2.5 h-2.5" /> En Curso
                                                    </span>
                                                ) : (
                                                    <span className="shrink-0 text-[10px] text-slate-500 whitespace-nowrap">{formatDate(order.scheduledDate)}</span>
                                                )}
                                            </div>
                                            <p className="text-slate-400 text-[10px] line-clamp-1 mt-0.5">{order.description}</p>
                                            {/* Sello de última exportación */}
                                            {order.lastExportedAt && (
                                                <p className="mt-1 text-[9px] text-amber-600 dark:text-amber-400 font-semibold flex items-center gap-1">
                                                    <FileText className="w-2.5 h-2.5" /> Exportado el {formatDate(order.lastExportedAt)}
                                                </p>
                                            )}
                                            {isInProgress && (
                                                <p className="mt-0.5 text-[9px] text-orange-500">
                                                    {order.laborJournal?.length ?? 0} jornada(s) registrada(s)
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                            {clientOrders.length === 0 && (
                                <p className="text-center py-6 text-slate-500 text-xs">No hay órdenes disponibles en este periodo.</p>
                            )}
                        </div>
                    </div>

                    {/* Aviso condicional para partes en curso */}
                    {hasInProgressSelected && (
                        <div className="flex items-start gap-2 p-3 bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-700 rounded-xl">
                            <AlertTriangle className="w-4 h-4 text-orange-500 shrink-0 mt-0.5" />
                            <p className="text-xs text-orange-700 dark:text-orange-300">
                                <span className="font-bold">Certificación Parcial.</span> Este informe incluye partes que aún están en curso. Los materiales mostrados corresponden al consumo registrado a fecha de hoy. El informe final se generará al completar la obra.
                            </p>
                        </div>
                    )}
                </div>

                <div className="flex gap-3 mt-6">
                    <button onClick={onClose} disabled={isGenerating} className="flex-1 py-3 text-slate-600 dark:text-slate-400 font-bold border border-slate-200 dark:border-dark-border rounded-xl hover:bg-slate-100 dark:hover:bg-dark-border hover:text-slate-900 dark:hover:text-white transition-colors text-sm disabled:opacity-50">Cancelar</button>
                    <button
                        onClick={() => runWithGenerateLock(async () => {
                            await onGeneratePDF(client, selectedOrders, dateStart || undefined, dateEnd || undefined);
                        })}
                        disabled={selectedOrders.length === 0 || isGenerating}
                        className="flex-[3] py-3 bg-emerald-600 text-white font-bold rounded-xl shadow-lg hover:bg-emerald-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:pointer-events-none flex items-center justify-center gap-2 text-sm"
                    >
                        {isGenerating ? (
                            <>
                                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                <span>Generando PDF...</span>
                            </>
                        ) : (
                            <>
                                <Download className="w-4 h-4" />
                                <span>Descargar Informe PDF</span>
                            </>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};
